import * as React from 'react';
import { useNavigate } from 'react-router-dom'; // Import useNavigate
import MainLayout from '../../components/Layout/MainLayout.tsx';
import RangeDatePicker from '../../components/Layout/DatePicker.tsx'; // *** NEW: Import RangeDatePicker ***
// *** NEW: Import lucide-react icons ***
import {
    ArrowRightLeft,
    Clock,
    BadgeDollarSign,
    BadgePercent,
    TrendingUp,
    CreditCard,
    HelpCircle // Fallback icon
} from 'lucide-react';

// Currency mapping (CODE) - Removed unused currencyMap

// *** NEW: Currency SYMBOL mapping ***
const currencySymbolMap: { [key: string]: string } = {
    'UAE': 'AED',
    'KSA': 'SAR',
    'Qatar': 'QAR',
    'Kuwait': 'KWD',
    'Bahrain': 'BHD',
    'All': '$', // Symbol for 'All' countries
};

// Helper to format amount with currency symbol and adjust based on country
// **Note**: The baseAmount is assumed to be in a base currency (e.g., USD).
// The factor converts it to the local currency for display.
// The 'All' country type will display in the base currency symbol ($).
const formatCurrency = (baseAmount: number, country: string): string => {
    // *** UPDATED: Use currencySymbolMap for the symbol ***
    const symbol = currencySymbolMap[country] || '$'; // Default to '$' if country not in symbol map or is 'All'

    // Simple adjustment factor for demonstration - **Assume baseAmount is USD**
    let factor = 1;
    if (country !== 'All') {
        switch (country) {
            // Updated plausible exchange rates (approximate)
            case 'KSA': factor = 3.75; break; // SAR
            case 'Qatar': factor = 3.64; break; // QAR
            case 'Kuwait': factor = 0.31; break; // KWD (Note: 1 KWD > 1 USD)
            case 'Bahrain': factor = 0.38; break; // BHD (Note: 1 BHD > 1 USD)
            case 'UAE': factor = 3.67; break;  // AED
            default: factor = 1; break; // Default factor (USD)
        }
    }

    // Apply factor only if NOT 'All'
    const adjustedAmount = country === 'All' ? baseAmount : baseAmount * factor;
    // Format with symbol, commas, and round to nearest integer (no decimals)
    return `${symbol} ${Math.round(adjustedAmount).toLocaleString()}`;
};

// *** NEW: Interface for Graph Data Point ***
interface GraphDataPoint {
    name: string;
    value: number;
}

// *** NEW: Interface for Transaction Row (Moved up) ***
interface TransactionRow {
    type: string;
    credit?: number; // Optional credit amount
    debit?: number;  // Optional debit amount
    category?: 'CR' | 'DR' | 'Manual corrections'; // *** NEW: Explicit category from image ***
    isTotal?: boolean; // Flag for the 'Total' row
    isNetBalance?: boolean; // Flag for the 'Net Balance' row
    categoryOverride?: string; // Optional category override - REMOVED, use explicit category now
}

// *** NEW: Placeholder for Balance Graph (Updated Styles) ***
const BalanceGraph: React.FC<{ data?: GraphDataPoint[] }> = ({ data }) => {
    const graphHeight = 80; // Slightly reduced height
    const graphWidth = 300; // Assume a fixed width for SVG path calculation
    const svgRef = React.useRef<SVGSVGElement>(null);
    const [hoveredIndex, setHoveredIndex] = React.useState<number | null>(null);
    const [hoverPosition, setHoverPosition] = React.useState<{ x: number; y: number } | null>(null);

    // Handle case where data is undefined or empty early
    if (!data || data.length === 0) {
        return (
            <div style={{ marginTop: '20px', height: `${graphHeight}px`, position: 'relative' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#9ca3af', fontSize: '13px', backgroundColor: '#f9fafb', borderRadius: '4px' }}>
                    No data available for graph
                </div>
            </div>
        );
    }

    // Now data is guaranteed to be GraphDataPoint[] and not empty
    const values = data.map((d) => d.value);
    const maxValue = Math.max(...values);
    const minValue = Math.min(...values);
    const range = maxValue - minValue <= 0 ? 1 : maxValue - minValue; // Avoid division by zero

    // Function to calculate y-coordinate based on value
    const calculateY = (value: number) => graphHeight - ((value - minValue) / range) * graphHeight;

    // Helper function to calculate control points for cubic Bezier curve
    const getControlPoints = (p0: {x: number, y: number}, p1: {x: number, y: number}, p2: {x: number, y: number}, tension = 0.3) => {
        // For the first point, use p1 as p0
        const d01 = Math.sqrt(Math.pow(p1.x - p0.x, 2) + Math.pow(p1.y - p0.y, 2));
        // For the last point, use p1 as p2
        const d12 = Math.sqrt(Math.pow(p2.x - p1.x, 2) + Math.pow(p2.y - p1.y, 2));

        const fa = tension * d01 / (d01 + d12 || 1); // Handle potential division by zero if d01+d12 is 0
        const fb = tension * d12 / (d01 + d12 || 1);

        const cp1x = p1.x - fa * (p2.x - p0.x);
        const cp1y = p1.y - fa * (p2.y - p0.y);
        const cp2x = p1.x + fb * (p2.x - p0.x);
        const cp2y = p1.y + fb * (p2.y - p0.y);

        return { cp1: { x: cp1x, y: cp1y }, cp2: { x: cp2x, y: cp2y } };
    };

    // Function to generate SVG path data for line and area using Bezier curves
    const generatePath = (area = false) => {
        if (!data || data.length < 2) return ''; // Need at least 2 points for a line/curve

        const points = data.map((d, i) => ({
            x: (i / (data.length - 1)) * graphWidth,
            y: calculateY(d.value)
        }));

        let path = `M ${points[0].x},${points[0].y}`;

        for (let i = 0; i < points.length - 1; i++) {
            const p0 = i === 0 ? points[i] : points[i - 1]; // Previous point (or current if first)
            const p1 = points[i]; // Current point
            const p2 = points[i + 1]; // Next point
            const p3 = i === points.length - 2 ? points[i + 1] : points[i + 2]; // Point after next (or next if last segment)

            const controls1 = getControlPoints(p0, p1, p2);
            const controls2 = getControlPoints(p1, p2, p3);

            path += ` C ${controls1.cp2.x},${controls1.cp2.y} ${controls2.cp1.x},${controls2.cp1.y} ${p2.x},${p2.y}`;
        }

        if (area) {
            path += ` L ${graphWidth},${graphHeight} L 0,${graphHeight} Z`;
        }
        return path;
    };

    const handleMouseMove = (event: React.MouseEvent<SVGSVGElement>) => {
        if (!svgRef.current || data.length < 1) return;

        const svgRect = svgRef.current.getBoundingClientRect();
        const mouseX = event.clientX - svgRect.left;

        // Calculate the closest data point index based on mouseX position
        // Clamp index to valid range
        const index = Math.max(0, Math.min(data.length - 1, Math.round((mouseX / graphWidth) * (data.length - 1))));

        // No need for ?. or assertion now
        const pointX = (index / (data.length - 1)) * graphWidth;
        const pointY = calculateY(data[index].value);
        setHoveredIndex(index);
        setHoverPosition({ x: pointX, y: pointY });
    };

    const handleMouseLeave = () => {
        setHoveredIndex(null);
        setHoverPosition(null);
    };

    const lineColor = '#059669'; // Darker Green (Tailwind Green 600)
    const areaGradientId = 'areaGradient';
    const gradientColor = '#C0F0CF'; // Requested gradient color
    const pointRadius = 4; // Size of the hover circle marker
    const pointStrokeWidth = 1.5; // Stroke width of the hover circle marker
    const tooltipOffsetY = 12; // Offset the tooltip above the point
    const tooltipFontSize = 11; // Slightly smaller tooltip text

    // No need for ?. or assertion now
    const hoveredValue = hoveredIndex !== null ? data[hoveredIndex].value : null;
    // *** UPDATED: Format hovered value using the $ symbol as graph data is assumed base currency ***
    const formattedHoveredValue = hoveredValue !== null ? `$ ${hoveredValue.toLocaleString()}` : '';

    return (
        <div style={{ marginTop: '20px', height: `${graphHeight}px`, position: 'relative' }}>
            {/* Remove the data.length >= 2 check here as it's handled partially by early return and generatePath */}
                 <svg
                    ref={svgRef}
                    width="100%"
                    height={graphHeight}
                    viewBox={`0 0 ${graphWidth} ${graphHeight}`}
                    preserveAspectRatio="none"
                    style={{ display: 'block', overflow: 'visible' }} // Allow overflow for tooltip
                    onMouseMove={handleMouseMove}
                    onMouseLeave={handleMouseLeave}
                 >
                     <defs>
                        <linearGradient id={areaGradientId} x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor={gradientColor} stopOpacity={0.4}/>
                            <stop offset="100%" stopColor={gradientColor} stopOpacity={0.05}/>
                        </linearGradient>
                    </defs>
                    {/* Area Fill with Gradient */}
                    <path d={generatePath(true)} fill={`url(#${areaGradientId})`} />
                    {/* Line */}
                    <path d={generatePath(false)} fill="none" stroke={lineColor} strokeWidth="2" />

                    {/* Hover Indicator */}
                    {hoverPosition && hoveredIndex !== null && hoveredValue !== null && (
                        <g>
                            {/* Vertical Line */}
                            <line
                                x1={hoverPosition.x} y1={0}
                                x2={hoverPosition.x} y2={graphHeight}
                                stroke="#9ca3af" strokeWidth="1" strokeDasharray="3,3"
                            />
                            {/* Marker Circle */}
                            <circle
                                cx={hoverPosition.x} cy={hoverPosition.y} r={pointRadius}
                                fill={lineColor} stroke="#ffffff" strokeWidth={pointStrokeWidth}
                            />
                            {/* Tooltip - Basic implementation */}
                            <text
                                x={hoverPosition.x}
                                y={hoverPosition.y - tooltipOffsetY} // Position above the marker
                                fill="#111827" // Dark text
                                fontSize={tooltipFontSize}
                                fontWeight="500"
                                textAnchor="middle" // Center text
                                style={{ pointerEvents: 'none' }} // Prevent text from interfering with mouse events
                            >
                                {/* *** UPDATED: Use formatted hovered value *** */}
                                {formattedHoveredValue}
                            </text>
                        </g>
                    )}
                </svg>
            {/* Render nothing or a placeholder if data length < 2? Currently handled by generatePath returning '' */}
            {/* The previous check rendered "Not enough data for graph" */} 
            {data.length < 2 && (
                 <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#9ca3af', fontSize: '13px', backgroundColor: '#f9fafb', borderRadius: '4px', position: 'absolute', top: 0, left: 0, width: '100%' }}>
                    Not enough data for graph
                </div>
            )}
        </div>
    );
};

// *** NEW: Component for the large Total Balance card with graph (Updated Styles) ***
interface TotalBalanceCardProps {
    title: string;
    amount: string;
    change?: string; // e.g., "▲ +$12,000" - **Assumed to be in base currency ($) before formatting**
    graphData?: GraphDataPoint[]; // Use GraphDataPoint[]
    selectedCountry: string; // Add selectedCountry prop
}

const TotalBalanceCard: React.FC<TotalBalanceCardProps> = ({ title, amount, change, graphData, selectedCountry }) => {
    const cardBgColor = '#ffffff';
    const cardBorderColor = '#f3f4f6'; // Lighter border
    const subtleShadow = '0 1px 2px 0 rgba(0, 0, 0, 0.03)'; // More subtle shadow
    const amountColor = '#111827'; // Near black
    const textColor = '#374151'; // *** UPDATED: Darker gray for title ***
    const lightTextColor = '#9ca3af'; // Lighter gray for info icon
    const greenColor = '#10b981';
    const redColor = '#f43f5e';
    const changeDirection = change?.includes('▲') ? '▲' : (change?.includes('▼') ? '▼' : '');
    const changeColor = changeDirection === '▲' ? greenColor : (changeDirection === '▼' ? redColor : lightTextColor);

    // Extract numeric value from change string (handle potential currency symbols/commas)
    const changeValueMatch = change?.match(/([+-]?[\d,]+(?:\.\d+)?)/);
    // ** Assume the change value is in the base currency ($) **
    const changeBaseNumericValue = changeValueMatch ? parseFloat(changeValueMatch[1].replace(/,/g, '')) : 0;

    // Format the change value using the selected country's currency logic
    // Pass the absolute base value to the formatter
    const formattedChangeValue = formatCurrency(Math.abs(changeBaseNumericValue), selectedCountry);

    const baseStyle: React.CSSProperties = {
        padding: '20px', // Slightly reduced padding
        borderRadius: '8px', // Standard border radius
        backgroundColor: cardBgColor,
        boxShadow: subtleShadow,
        border: `1px solid ${cardBorderColor}`,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between', // Push graph to bottom
    };

    return (
        <div
            style={baseStyle}
        >
            <div> {/* Top section */}
                {/* Title - Adjusted style */}
                <div style={{ marginBottom: '4px', color: textColor, fontSize: '13px', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '5px' }}>
                    {title}
                    {/* Mercury-style Info Icon */}
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor" color={lightTextColor}><path fillRule="evenodd" d="M8 1.5a6.5 6.5 0 100 13 6.5 6.5 0 000-13zM0 8a8 8 0 1116 0A8 8 0 010 8zm6.5-.25A.75.75 0 017.25 7h1.5a.75.75 0 010 1.5h-.75v2.5h.75a.75.75 0 010 1.5h-1.5a.75.75 0 01-.75-.75V8.25H6.5a.75.75 0 01-.75-.75zM8 5.5a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd"/></svg>
                </div>
                {/* Amount - Larger */}
                <div style={{ fontSize: '28px', fontWeight: '600', marginBottom: '2px', color: amountColor, lineHeight: '1.2' }}>{amount}</div>
                {/* Change - Smaller */}
                {change && changeDirection && (
                    <span style={{ color: changeColor, display: 'flex', alignItems: 'center', gap: '3px', fontSize: '12px', fontWeight: '500' }}>
                        {changeDirection === '▲' && <svg width="10" height="10" fill={greenColor} viewBox="0 0 16 16"><path d="M7.247 4.86l-4.796 5.481c-.566.647-.106 1.659.753 1.659h9.592a1 1 0 0 0 .753-1.659l-4.796-5.48a1 1 0 0 0-1.506 0z"/></svg>}
                        {changeDirection === '▼' && <svg width="10" height="10" fill={redColor} viewBox="0 0 16 16"><path d="M7.247 11.14L2.451 5.658C1.885 5.013 2.345 4 3.204 4h9.592a1 1 0 0 1 .753 1.659l-4.796 5.48a1 1 0 0 1-1.506 0z"/></svg>}
                        {/* Use the fully formatted value including currency */}
                        {/* *** UPDATED: Ensure +/- sign is added correctly *** */}
                        {`${changeDirection === '▲' ? '+' : '-'}${formattedChangeValue}`}
                    </span>
                )}
            </div>
             {/* Bottom section - Graph */}
            <BalanceGraph data={graphData} />
        </div>
    );
};

// *** NEW: Component for the smaller Sub Wallet cards (Updated Styles) ***
interface SubWalletCardProps {
    title: string; // Product name
    amount: string;
}

const SubWalletCard: React.FC<SubWalletCardProps> = ({ title, amount }) => {
    const [isHovered, setIsHovered] = React.useState(false); // State for hover effect
    const cardBorderColor = '#e5e7eb'; // Border for separation
    const amountColor = '#374151'; // Darker Gray for amount
    const textColor = '#374151'; // Darker Gray for title
    const iconColor = '#9ca3af'; // Light gray for icon - Match inactive sidebar icon color (#64748b) ? Let's keep #9ca3af for now as it's slightly lighter as per screenshot.
    const hoverBgColor = '#f9fafb'; // Light gray background on hover

    const baseStyle: React.CSSProperties = {
        borderBottom: `1px solid ${cardBorderColor}`, // Use bottom border instead of full border
        padding: '12px 4px', // Slightly increased vertical padding
        backgroundColor: isHovered ? hoverBgColor : 'transparent', // Apply hover background
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        cursor: 'pointer', // Add pointer cursor
        transition: 'background-color 0.15s ease-in-out', // Smooth transition for hover
        // Removed marginBottom, handled by border
    };

    // *** NEW: Function to get the appropriate icon based on title using lucide-react ***
    const getWalletIcon = (walletTitle: string) => {
        let IconComponent: React.ElementType = HelpCircle; // Default fallback

        switch (walletTitle) {
            case 'BNPL':
                IconComponent = Clock;
                break;
            case 'Acquiring':
                IconComponent = ArrowRightLeft;
                break;
            case 'Prepaid Ads':
                 IconComponent = BadgeDollarSign;
                break;
            case 'Postpaid Ads':
                IconComponent = BadgePercent;
                break;
            case 'Capital':
                IconComponent = TrendingUp;
                break;
            case 'Tabby Card':
                IconComponent = CreditCard;
                break;
            default:
                IconComponent = HelpCircle; // Use HelpCircle as a fallback
        }

        // Render the selected icon component with sidebar styling
        return (
            <IconComponent
                size={16} // Keep the original requested size (16px)
                strokeWidth={1.5} // Match sidebar inactive stroke width
                color={iconColor} // Use the defined icon color
            />
        );
    };

    // NEW: Access navigate function from props or context if needed, or define within the component where needed
    const navigate = useNavigate(); // Make sure useNavigate is imported if used here directly

    // NEW: Click handler
    const handleClick = () => {
        // Navigate based on wallet title
        if (title === 'BNPL') {
            navigate('/wallets/bnpl'); // Navigate to the BNPL specific page
        } else if (title === 'Acquiring') {
            navigate('/wallets/acquiring'); // Navigate to the Acquiring specific page
        } else if (title === 'Prepaid Ads') {
            navigate('/wallets/ads-prepaid'); // Navigate to the Ads Prepaid wallet page
        } else if (title === 'Postpaid Ads') {
            navigate('/wallets/ads-postpaid'); // Navigate to the Ads Postpaid wallet page
        } else if (title === 'Capital') { // <<<--- ADDED THIS CONDITION
            navigate('/wallets/credit'); // Navigate to the Credit wallet page
        } else if (title === 'Tabby Card') {
            navigate('/wallets/tabbycard'); // Navigate to the Tabby Card wallet page
        } else {
            console.log(`Navigate to ${title} wallet page (placeholder)`);
            // Placeholder for other wallet navigation logic
            // const route = `/wallets/${title.toLowerCase().replace(' ', '-')}`;
            // navigate(route);
        }
    };

    return (
        <div
            style={baseStyle}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            onClick={handleClick} // Add click handler
        >
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                 {/* *** UPDATED: Use getWalletIcon function *** */}
                 {getWalletIcon(title)}
                 <span style={{ fontSize: '13px', fontWeight: '500', color: textColor }}>{title}</span>
            </div>
            <span style={{ fontSize: '13px', fontWeight: '500', color: amountColor }}>{amount}</span>
        </div>
    );
};


// Helper component for Expandable Section (can be moved)
interface ExpandableSectionProps {
    title: string;
    children?: React.ReactNode; // Content to show when expanded
}

const ExpandableSection: React.FC<ExpandableSectionProps> = ({ title }) => {
    // Basic non-functional placeholder
    const [isOpen, setIsOpen] = React.useState(false);
    return (
        <div style={{ border: '1px solid #e5e7eb', borderRadius: '8px', marginBottom: '10px', backgroundColor: 'white' }}> {/* Lighter border */}
            <button onClick={() => setIsOpen(!isOpen)} style={{ width: '100%', textAlign: 'left', padding: '15px', border: 'none', background: 'none', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontWeight: '500', color: '#374151' }}> {/* Adjusted text color */}
                {title}
                <span style={{ color: '#9ca3af' }}>{isOpen ? '▲' : '▼'}</span> {/* Lighter arrow color */}
            </button>
            {/* {isOpen && <div style={{ padding: '0 15px 15px 15px', borderTop: '1px solid #e5e7eb' }}> Placeholder Content </div>} */} {/* Lighter top border */}
        </div>
    );
};

// Helper component for Status Badge (can be moved)
const StatusBadge: React.FC<{ status: 'Active' | 'Inactive' | 'Pending' }> = ({ status }) => {
  let backgroundColor = '';
  let color = '';
  // Updated badge colors for lighter theme
  switch (status) {
    case 'Active': backgroundColor = '#ecfdf5'; color = '#059669'; break; // Lighter Green
    case 'Inactive': backgroundColor = '#f8fafc'; color = '#64748b'; break; // Lighter Gray
    case 'Pending': backgroundColor = '#fefce8'; color = '#d97706'; break; // Lighter Yellow/Amber
  }
  return <span style={{ backgroundColor, color, padding: '3px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '500', textTransform: 'capitalize' }}>{status}</span>;
};

// Helper component for Dropdown Filter
interface DropdownFilterProps {
    label: string;
    options: string[];
    selectedValue: string;
    onChange: (value: string) => void;
}

const DropdownFilter: React.FC<DropdownFilterProps> = ({ label, options, selectedValue, onChange }) => {
    const [isOpen, setIsOpen] = React.useState(false);
    const [hoveredOption, setHoveredOption] = React.useState<string | null>(null); // State for hover
    const dropdownRef = React.useRef<HTMLDivElement>(null); // Ref for the dropdown container

    const handleSelect = (option: string) => {
        onChange(option);
        setIsOpen(false);
    };

    // Effect to handle clicks outside the dropdown
    React.useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        // Add listener when dropdown is open
        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        } else {
            // Remove listener when dropdown is closed
            document.removeEventListener('mousedown', handleClickOutside);
        }

        // Cleanup listener on component unmount
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]); // Re-run effect when isOpen changes

    return (
        <div style={{ position: 'relative' }} ref={dropdownRef}> {/* Attach ref here */}
            <button
                style={{
                    padding: '8px 12px',
                    borderRadius: '6px',
                    border: '1px solid #d1d5db', // Keep light gray border
                    backgroundColor: 'white',
                    fontSize: '14px',
                    color: '#374151',
                    cursor: 'pointer',
                    minWidth: '140px',
                    textAlign: 'left',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: '6px',
                    transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out' // Add transition
                }}
                onClick={() => setIsOpen(!isOpen)}
                onMouseEnter={(e) => e.currentTarget.style.borderColor = '#adb5bd'} // Slightly darken border on hover
                onMouseLeave={(e) => e.currentTarget.style.borderColor = '#d1d5db'} // Restore border color
            >
                {label}: <span style={{ fontWeight: '500', color: '#1f2937' }}>{selectedValue}</span>
                {/* Custom Arrow */}
                <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="#6b7280" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d={isOpen ? "m18 14-6-6-6 6" : "m6 10 6 6 6-6"} />
                </svg>
            </button>
            {isOpen && (
                <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '6px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)', zIndex: 10, minWidth: '160px', padding: '4px 0' }}> {/* Adjusted shadow and margin */}
                    <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                        {options.map(option => (
                            <li
                                key={option}
                                style={{
                                    padding: '8px 16px',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    color: '#374151',
                                    backgroundColor: hoveredOption === option ? '#f9fafb' : 'white' // Lighter hover background
                                }}
                                onClick={() => handleSelect(option)}
                                onMouseEnter={() => setHoveredOption(option)} // Set hover state
                                onMouseLeave={() => setHoveredOption(null)}   // Clear hover state
                            >
                                {option}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
};


const MerchantWalletDashboard: React.FC = () => {
  // State for filters and actions dropdown
  // REMOVED: const [selectedTimeRange, setSelectedTimeRange] = React.useState<string>('This Month');
  const [selectedProduct, setSelectedProduct] = React.useState<string>('All Products');
  const [selectedCountry, setSelectedCountry] = React.useState<string>('All'); // Default to 'All'
  // REMOVED: const [isActionsOpen, setIsActionsOpen] = React.useState<boolean>(false);
  // REMOVED: const actionsDropdownRef = React.useRef<HTMLDivElement>(null); // Ref for the actions dropdown
  // REMOVED: const [hoveredAction, setHoveredAction] = React.useState<string | null>(null); // State for action hover
  const [selectedPartnerFilter, setSelectedPartnerFilter] = React.useState<string>('All'); // State for quick filters
  const [hoveredRowIndex, setHoveredRowIndex] = React.useState<string | number | null>(null); // State for row hover (can be string or number now)
  const [searchQuery, setSearchQuery] = React.useState<string>(''); // *** NEW: State for search query ***

  // *** NEW: Date Filter State (like PayoutsPage) ***
  const [selectedDateRange, setSelectedDateRange] = React.useState<string>('All Time');
  const [customStartDate, setCustomStartDate] = React.useState<Date | null>(null);
  const [customEndDate, setCustomEndDate] = React.useState<Date | null>(null);
  const [isDatePickerVisible, setIsDatePickerVisible] = React.useState<boolean>(false);

  const navigate = useNavigate(); // Get navigate function

  // *** NEW: Mock Graph Data (Updated for more realism) ***
  const mockGraphData: GraphDataPoint[] = React.useMemo(() => {
      // Generate more realistic trend data (e.g., slight upward trend with volatility)
      const baseValue = 1850000; // Starting point in base currency ($)
      const trendFactor = 15000; // Average increase per point
      const volatility = 400000; // Max random fluctuation range (+/- volatility/2)
      const numPoints = 20; // More data points

      let currentValue = baseValue;
      return Array.from({ length: numPoints }, (_, i): GraphDataPoint => {
          // Add trend component
          currentValue += trendFactor * (Math.random() * 1.5); // Randomize trend slightly
          // Add volatility component
          const noise = (Math.random() - 0.5) * volatility;
          const finalValue = currentValue + noise;
          return {
              name: `Point ${i + 1}`,
              // Ensure value is reasonable and positive, round it
              value: Math.max(50000, Math.round(finalValue))
          };
      });
  }, []);

  // *** NEW: Memoize graph data for the credit balance card ***
  const creditGraphData = React.useMemo(() => {
    // Use a slice/variation for the second graph for visual difference
    // Apply the transformation logic here, it will only run when mockGraphData changes
    return mockGraphData
      .map(p => ({
        ...p,
        // Use a stable transformation if possible, or ensure Math.random() is acceptable for mock data
        // For stability, let's base the variation on the original value + index, avoiding Math.random() if flicker is the main issue.
        // Example stable variation:
        value: Math.max(10000, Math.round(p.value * 0.75 + (p.name.length % 5) * 5000)) // More stable variation
        // If some randomness is still desired but stable per render:
        // value: Math.max(10000, Math.round(p.value * 0.8 + parseInt(p.name.split(' ')[1]) * 1000 % 50000)) // Another stable variation example
      }))
      .slice(0, 15); // Keep the slice if desired
  }, [mockGraphData]); // Dependency array ensures recalculation only if mockGraphData changes

  // Mock data - replace with actual data fetching later
  // *** UPDATED Partner Data (more realistic base values in $) ***
  const partnersData = React.useMemo(() => [
    { name: 'Amazon', location: 'UAE', products: ['BNPL'], baseDebit: 650000, baseCredit: 80000, status: 'Active', risk: '85%', lastPayout: '2 days ago', logoUrl: 'https://upload.wikimedia.org/wikipedia/commons/a/a9/Amazon_logo.svg' },
    { name: 'Shopify', location: 'KSA', products: ['BNPL', 'Tabby Card'], baseDebit: 880000, baseCredit: 150000, status: 'Active', risk: '92%', lastPayout: '1 day ago', logoUrl: 'https://upload.wikimedia.org/wikipedia/commons/0/0e/Shopify_logo_2018.svg' },
    { name: 'Noon', location: 'UAE', products: ['BNPL', 'Prepaid Ads', 'Postpaid Ads'], baseDebit: 1200000, baseCredit: 450000, status: 'Active', risk: '78%', lastPayout: '3 days ago', logoUrl: 'https://upload.wikimedia.org/wikipedia/commons/c/ca/Noon_Website_Logo.svg' }, // Updated logo
    { name: 'Namshi', location: 'KSA', products: ['BNPL', 'Capital'], baseDebit: 420000, baseCredit: 110000, status: 'Inactive', risk: '65%', lastPayout: '5 days ago', logoUrl: null }, // No suitable logo found
    { name: 'Carrefour', location: 'UAE', products: ['BNPL', 'Acquiring', 'Tabby Card'], baseDebit: 950000, baseCredit: 210000, status: 'Active', risk: '88%', lastPayout: 'yesterday', logoUrl: 'https://upload.wikimedia.org/wikipedia/commons/5/5b/Carrefour_logo.svg' },
    { name: 'IKEA', location: 'KSA', products: ['BNPL', 'Capital', 'Prepaid Ads'], baseDebit: 530000, baseCredit: 95000, status: 'Active', risk: '90%', lastPayout: '4 days ago', logoUrl: 'https://upload.wikimedia.org/wikipedia/commons/c/c5/Ikea_logo.svg' },
    { name: 'Adidas', location: 'UAE', products: ['BNPL', 'Tabby Card', 'Postpaid Ads'], baseDebit: 380000, baseCredit: 70000, status: 'Pending', risk: '75%', lastPayout: '6 days ago', logoUrl: 'https://upload.wikimedia.org/wikipedia/commons/2/20/Adidas_Logo.svg' },
    { name: 'H&M', location: 'Qatar', products: ['BNPL', 'Acquiring'], baseDebit: 290000, baseCredit: 60000, status: 'Active', risk: '82%', lastPayout: '2 days ago', logoUrl: 'https://upload.wikimedia.org/wikipedia/commons/5/53/H%26M-Logo.svg' },
    { name: 'Xcite', location: 'Kuwait', products: ['BNPL'], baseDebit: 210000, baseCredit: 45000, status: 'Active', risk: '80%', lastPayout: '1 day ago', logoUrl: 'https://media.xcite.com/media/logo/stores/1/New-Xcite-Logo-EnAr.png' }, // PNG found
    { name: 'Jarir Bookstore', location: 'Kuwait', products: ['BNPL', 'Capital'], baseDebit: 260000, baseCredit: 55000, status: 'Active', risk: '88%', lastPayout: '5 days ago', logoUrl: 'https://upload.wikimedia.org/wikipedia/en/5/58/Jarir_Bookstore_logo.svg' },
    { name: 'Sharaf DG', location: 'Bahrain', products: ['BNPL', 'Acquiring'], baseDebit: 180000, baseCredit: 30000, status: 'Pending', risk: '70%', lastPayout: '3 days ago', logoUrl: 'https://uae.sharafdg.com/wp-content/uploads/2020/11/logo-sharafdg.png' }, // PNG found
    { name: 'LuLu Hypermarket', location: 'Bahrain', products: ['BNPL'], baseDebit: 350000, baseCredit: 85000, status: 'Active', risk: '91%', lastPayout: 'today', logoUrl: null }, // No suitable logo found
    { name: 'Talabat', location: 'Qatar', products: ['Acquiring', 'Prepaid Ads'], baseDebit: 410000, baseCredit: 75000, status: 'Active', risk: '84%', lastPayout: 'yesterday', logoUrl: 'https://upload.wikimedia.org/wikipedia/commons/b/b3/Talabat_logo.svg' },
  ], []); // Empty dependency array as this data is static within the component scope

  // Define the interface for a transaction row
  // REMOVED: Moved interface definition outside component scope

  // Data based on the BNPL image - Using base numbers, formatting will add currency/commas
  // REMOVED bnplTransactions definition from here as it's now defined globally and renamed

  const productList = ['BNPL', 'Acquiring', 'Prepaid Ads', 'Capital', 'Tabby Card', 'Postpaid Ads']; // Added Prepaid Ads
  // REMOVED: const timeRangeOptions = ['Yesterday', 'This Month', 'Last Month', 'This Quarter', 'This Year']; // Define options here // *** UPDATED Last Day to Yesterday ***
  // *** NEW: Date range options (like PayoutsPage) ***
  const dateRangeOptions = ['All Time', 'Last 7 Days', 'Last 30 Days', 'Last 90 Days', 'Custom date'];
  const countryOptions = ['All', 'UAE', 'KSA', 'Kuwait', 'Bahrain', 'Qatar']; // Add 'All', Define country options

  // *** NEW: Define wallet data structures (Updated realistic base amounts in $) ***
  const debitWallets = [
    { title: 'BNPL', baseAmount: 4250000 },
    { title: 'Acquiring', baseAmount: 2800000 },
    { title: 'Prepaid Ads', baseAmount: 950000 },
  ];
  const creditWallets = [
    { title: 'Postpaid Ads', baseAmount: 650000 },
    { title: 'Capital', baseAmount: 1750000 },
    { title: 'Tabby Card', baseAmount: 1100000 },
  ];
  // *** UPDATED: Calculate totals dynamically from wallets ***
  const totalDebitBase = debitWallets.reduce((sum, wallet) => sum + wallet.baseAmount, 0);
  const totalCreditBase = creditWallets.reduce((sum, wallet) => sum + wallet.baseAmount, 0);


  // Filter partners based on selected product, selected country, quick filter, AND search query
  const filteredPartners = React.useMemo(() => {
    return partnersData.filter(partner => {
        // Check product filter
        const productMatch = selectedProduct === 'All Products' || partner.products.includes(selectedProduct);
        // Check country filter
        const countryMatch = selectedCountry === 'All' || partner.location === selectedCountry;
        // Check quick filter
        const quickFilterMatch = selectedPartnerFilter === 'All' ||
            (selectedPartnerFilter === 'Active' && partner.status === 'Active') ||
            (selectedPartnerFilter === 'Requires Attention' && partner.status === 'Pending') ||
            (selectedPartnerFilter === 'BNPL' && partner.products.includes('BNPL'));
        // *** NEW: Check search query ***
        const searchMatch = searchQuery === '' || partner.name.toLowerCase().includes(searchQuery.toLowerCase());

        return productMatch && countryMatch && quickFilterMatch && searchMatch;
    });
  }, [selectedProduct, selectedCountry, selectedPartnerFilter, searchQuery, partnersData]); // Added searchQuery dependency

  // NEW: Handle partner row click
  const handlePartnerClick = (partnerName: string) => {
      console.log(`Navigate to profile for: ${partnerName}`);
      // Convert partner name to a URL-friendly slug
      const slug = partnerName.toLowerCase().replace(/\\s+/g, '-').replace(/[^a-z0-9-]/g, '');
      // Use navigate to go to the partner details page
      navigate(`/partners/${slug}`);
  };

  // --- Select Transaction Data based on selectedCountry and selectedProduct --- 
  // *** UPDATED: Add Date Filtering ***
  const displayedTransactionsData = React.useMemo(() => {
    const productDataMap: { [product: string]: TransactionRow[] } = {};

    // Determine which products to display
    const productsToShow: string[] = [];
    if (selectedProduct === 'All Products') {
        productsToShow.push(...productList);
    } else if (productList.includes(selectedProduct)) {
        productsToShow.push(selectedProduct);
    }

    // Populate the map with data for the selected products and country
    productsToShow.forEach(product => {
        productDataMap[product] = getProductTransactions(product, selectedCountry);
    });

    // Convert map to array structure
    let allTransactions = Object.entries(productDataMap)
        .map(([product, transactions]) => ({
            product,
            transactions
        }));

    // --- Date filtering logic (simplified) ---
    // Note: TransactionRow doesn't have a date. We assume getProductTransactions 
    // implicitly handles date filtering based on selectedDateRange/customDates 
    // OR we need to add date filtering logic here if getProductTransactions 
    // always returns all data.
    // For now, we will assume getProductTransactions handles it based on its inputs 
    // (product, country), and we don't filter further by date here.
    // If date needs to be filtered client-side, the TransactionRow interface 
    // and data sources need to be updated with dates.
    
    // Filter out totals/net balance after potential date filtering (if implemented above)
    return allTransactions
        .map(pData => ({
            ...pData,
            transactions: pData.transactions.filter(tx => !tx.isTotal && !tx.isNetBalance)
        }))
        .filter(pData => pData.transactions.length > 0); // Remove products with no transactions

  }, [selectedCountry, selectedProduct, productList, selectedDateRange, customStartDate, customEndDate]); // *** UPDATED Dependencies ***

  // *** NEW: Split displayed data into Debit and Credit ***
  const allDisplayedTransactionsData = displayedTransactionsData; // Keep original name temporarily if needed elsewhere, or rename directly

  // *** MOVED UP: Define Debit/Credit product categories ***
  const debitProducts = ['BNPL', 'Acquiring', 'Prepaid Ads'];
  const creditProducts = ['Capital', 'Tabby Card', 'Postpaid Ads'];

  const displayedDebitTransactionsData = React.useMemo(() => {
      return allDisplayedTransactionsData.filter(pData => debitProducts.includes(pData.product));
  }, [allDisplayedTransactionsData, debitProducts]); // Added missing dependency: debitProducts

  const displayedCreditTransactionsData = React.useMemo(() => {
      return allDisplayedTransactionsData.filter(pData => creditProducts.includes(pData.product));
  }, [allDisplayedTransactionsData, creditProducts]); // Added missing dependency: creditProducts

  // *** NEW: Helper to format date for display (simplified) ***
  const simpleFormatDate = (date: Date | null): string => {
      if (!date) return '';
      return date.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric'
      });
  };

  // *** NEW: Get date range text for display ***
  const getDateRangeText = (): string => {
      if (selectedDateRange === 'Custom date') {
          if (customStartDate && customEndDate) {
              return `from ${simpleFormatDate(customStartDate)} to ${simpleFormatDate(customEndDate)}`;
          } else if (customStartDate) {
              return `from ${simpleFormatDate(customStartDate)}`;
          } else if (customEndDate) {
              return `up to ${simpleFormatDate(customEndDate)}`;
          } else {
              return 'for a custom date range';
          }
      } else if (selectedDateRange === 'All Time') {
          return 'for all time';
      } else {
          return `for the ${selectedDateRange.toLowerCase()}`;
      }
  };

  // *** NEW: Handlers for date filters (like PayoutsPage) ***
  const handleDateRangeChange = (value: string) => {
    setSelectedDateRange(value);
    if (value === 'Custom date') {
      setIsDatePickerVisible(true);
    } else {
      setIsDatePickerVisible(false);
      setCustomStartDate(null);
      setCustomEndDate(null);
    }
  };

  const handleCustomDateChange = (dates: [Date | null, Date | null]) => {
    const [start, end] = dates;
    setCustomStartDate(start);
    setCustomEndDate(end);
  };

  return (
    <MainLayout>
     {/* Use a very light gray background for the page */}
     <div style={{ padding: '24px' }}>
      {/* Header Area */}
      <div style={{ marginBottom: '24px' }}>
          {/* Top Row: Title & Description */}
          <div style={{ marginBottom: '20px' }}> {/* Increased margin */}
              {/* Updated heading styles */}
              <h1 style={{ margin: 0, fontSize: '28px', fontWeight: '600', color: '#111827', lineHeight: '1.2' }}>Merchant Wallet</h1>
              {/* Updated paragraph styles */}
              <p style={{ margin: '4px 0 0 0', color: '#6b7280', fontSize: '14px' }}>Operations ledger for all transactions across Tabby products and ERP</p>
          </div>

          {/* Bottom Row: Filters & Actions */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              {/* Left Side: Filters */}
              <div style={{ display: 'flex', gap: '12px' }}> {/* Row for filters */}
                  {/* *** UPDATED: Date Range Dropdown Filter *** */}
                  <DropdownFilter
                      label="Date"
                      options={dateRangeOptions}
                      selectedValue={selectedDateRange}
                      onChange={handleDateRangeChange}
                  />
                  {/* *** NEW: Conditionally render RangeDatePicker *** */}
                  {isDatePickerVisible && (
                      <div style={{ marginLeft: '-6px' /* Adjust positioning */ }}>
                        <RangeDatePicker
                          startDate={customStartDate}
                          endDate={customEndDate}
                          onChange={handleCustomDateChange}
                          id="dashboard-date-picker-trigger" // Use a unique ID if needed
                        />
                      </div>
                  )}
                  {/* Product Dropdown Filter */}
                  <DropdownFilter
                      label="Product"
                      options={['All Products', ...productList]}
                      selectedValue={selectedProduct}
                      onChange={setSelectedProduct}
                  />
                  {/* Country Dropdown Filter */}
                  <DropdownFilter
                      label="Country"
                      options={countryOptions}
                      selectedValue={selectedCountry}
                      onChange={setSelectedCountry}
                  />
              </div>

              {/* Right Side: Actions Button - Updated styling */}
              {/* REMOVED the entire Actions button div and its contents */}
          </div>
      </div>


      {/* Overview Section */}
      <section>
        {/* Updated heading style */}
        <h2 style={{ marginBottom: '16px', fontSize: '20px', fontWeight: '600', color: '#111827' }}>Overview</h2>

        {/* *** REVISED: 4-Column Grid Layout (Updated Styles) *** */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '20px', marginBottom: '30px', alignItems: 'stretch' }}>

            {/* Column 1: Total Debit Balance Card */}
            <TotalBalanceCard
                title="Debit Balance"
                amount={formatCurrency(totalDebitBase, selectedCountry)}
                change="▲ +$185400"
                graphData={mockGraphData}
                selectedCountry={selectedCountry}
            />

            {/* Column 2: Debit Sub-Wallets Stack with Frame (Updated Styles) */}
            <div style={{ border: '1px solid #f3f4f6', borderRadius: '8px', padding: '16px 16px 6px 16px', backgroundColor: '#ffffff', boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.03)', display: 'flex', flexDirection: 'column' }}>
                {/* *** UPDATED: Title style - Slightly bolder and larger *** */}
                <h3 style={{ fontSize: '14px', fontWeight: '600', color: '#1f2937', marginBottom: '12px', paddingLeft: '4px' }}>Debit Wallets</h3>
                <div style={{ flexGrow: 1 }}> {/* Allow sub-cards to take remaining space */}
                    {debitWallets.map((wallet, index) => (
                        <SubWalletCard
                            key={wallet.title}
                            title={wallet.title}
                            amount={formatCurrency(wallet.baseAmount, selectedCountry)}
                        />
                    ))}
                </div>
            </div>

            {/* Column 3: Total Credit Balance Card */}
            <TotalBalanceCard
                title="Credit Balance"
                amount={formatCurrency(totalCreditBase, selectedCountry)}
                change="▼ -$22750"
                // Use a different slice/variation for the second graph for visual difference
                // *** UPDATED: Pass the memoized creditGraphData ***
                graphData={creditGraphData}
                selectedCountry={selectedCountry}
            />

            {/* Column 4: Credit Sub-Wallets Stack with Frame (Updated Styles) */}
            <div style={{ border: '1px solid #f3f4f6', borderRadius: '8px', padding: '16px 16px 6px 16px', backgroundColor: '#ffffff', boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.03)', display: 'flex', flexDirection: 'column' }}>
                {/* *** UPDATED: Title style - Slightly bolder and larger *** */}
                <h3 style={{ fontSize: '14px', fontWeight: '600', color: '#1f2937', marginBottom: '12px', paddingLeft: '4px' }}>Credit Wallets</h3>
                <div style={{ flexGrow: 1 }}> {/* Allow sub-cards to take remaining space */}
                    {creditWallets.map((wallet, index) => (
                        <SubWalletCard
                            key={wallet.title}
                            title={wallet.title}
                            amount={formatCurrency(wallet.baseAmount, selectedCountry)}
                        />
                    ))}
                </div>
            </div>
        </div>

        {/* *** PREVIOUS Grid Layout - REMOVED *** */}
        {/*
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(450px, 1fr))', gap: '24px', marginBottom: '30px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '20px', alignItems: 'start' }}>
                <TotalBalanceCard
                    title="Debit Balance"
                    amount={formatCurrency(totalDebitBase, selectedCountry)}
                    change="▲ +$256,029.97" // Example static change
                    graphData={mockGraphData} // Pass mock data
                />
                <div>
                    <h3 style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '12px', paddingLeft: '4px' }}>Debit Wallets</h3>
                    {debitWallets.map(wallet => (
                        <SubWalletCard
                            key={wallet.title}
                            title={wallet.title}
                            amount={formatCurrency(wallet.baseAmount, selectedCountry)}
                        />
                    ))}
                </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '20px', alignItems: 'start' }}>
                 <TotalBalanceCard
                    title="Credit Balance"
                    amount={formatCurrency(totalCreditBase, selectedCountry)}
                    change="▼ -$5,100.50" // Example static change
                    graphData={mockGraphData.slice().reverse()} // Pass different mock data variation
                />
                <div>
                    <h3 style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '12px', paddingLeft: '4px' }}>Credit Wallets</h3>
                    {creditWallets.map(wallet => (
                        <SubWalletCard
                            key={wallet.title}
                            title={wallet.title}
                            amount={formatCurrency(wallet.baseAmount, selectedCountry)}
                        />
                    ))}
                </div>
            </div>
        </div>
        */}
      </section>

      {/* Transactions Section */}
       <section style={{ marginTop: '32px' }}>
         {/* Updated heading and button style */}
         <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
           {/* Left Side: Title and View All */}
           <div style={{ display: 'flex', alignItems: 'baseline', gap: '24px' }}> {/* Align by baseline, Add gap */}
             <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600', color: '#111827' }}>Aggregated Transactions</h2> {/* Renamed header */}
             <button
                style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#059669', fontSize: '14px', fontWeight: '500', padding: 0 }}
                onClick={() => navigate('/transactions')} // Add navigation onClick
             >
               View all &gt;
             </button>
           </div>
           {/* Right Side: Download Button */}
           <button style={{ padding: '6px 12px', borderRadius: '6px', border: '1px solid #d1d5db', background: 'white', cursor: 'pointer', fontSize: '14px', color: '#374151', display: 'flex', alignItems: 'center', gap: '4px' }}>
             <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M4.5 12.5H11.5V11.5H4.5V12.5ZM8 9.5L11 6.5H9V2.5H7V6.5H5L8 9.5Z" fill="#6B7280"/></svg> Download
           </button>
         </div>

         {/* *** NEW: Date Indication Text *** */}
         <div style={{ marginBottom: '16px', fontSize: '13px', color: '#6b7280' }}>
            Displaying aggregated transactions as end of <span style={{ fontWeight: 'bold' }}>{getDateRangeText()}</span>.
         </div>

         {/* *** NEW: Two-column grid for Transaction Tables *** */}
         <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '60px' }}>

             {/* Column 1: Debit Transaction Table Container */}
             {/* *** UPDATED: Removed horizontal padding from container *** */}
             <div style={{ backgroundColor: 'white', padding: '20px 0', borderRadius: '8px' }}>
                {/* Transaction Table - Debit Products */}
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                    <thead>
                        <tr style={{ borderBottom: '1px solid #e5e7eb', color: '#6b7280', textAlign: 'left' }}>
                            {/* *** UPDATED: Adjust widths if necessary for the new column layout *** */}
                            <th style={{ padding: '8px 6px', fontWeight: '500', width: '20%' }}>Product</th>
                            <th style={{ padding: '8px 6px', fontWeight: '500', width: '25%' }}>Transaction category</th>
                            <th style={{ padding: '8px 6px', fontWeight: '500', width: '35%' }}>Transaction type</th>
                            <th style={{ padding: '8px 6px', fontWeight: '500', textAlign: 'right', width: '20%' }}>Amount</th> {/* Ensure right align for header too */}
                        </tr>
                    </thead>
                    <tbody>
                        {displayedDebitTransactionsData.length === 0 ? ( // *** Use Debit Data ***
                             // Apply Partners Table Empty State Style
                             <tr>
                                 <td colSpan={4} style={{ textAlign: 'center', padding: '40px 20px', color: '#6b7280', borderTop: '1px solid #e5e7eb' }}>
                                    {/* Simple Document Search Icon */}
                                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: '16px', color: '#d1d5db' }}>
                                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                                        <polyline points="14 2 14 8 20 8"></polyline>
                                        {/* Removed search icon elements from document icon */}
                                        <line x1="8" y1="13" x2="16" y2="13"></line>
                                        <line x1="8" y1="17" x2="12" y2="17"></line>
                                    </svg>
                                    <p style={{ fontSize: '14px', fontWeight: '500', margin: 0, color: '#111827' }}>No Transactions Found</p> {/* Match text */}
                                    <p style={{ fontSize: '13px', color: '#9ca3af', marginTop: '4px' }}>Try adjusting your date, product, or country filters.</p> {/* Adjusted message */}
                                </td>
                            </tr>
                        ) : (
                            // *** START REFACTORED LOGIC ***
                            displayedDebitTransactionsData.flatMap(({ product, transactions }, productIndex) => { // *** Use Debit Data ***
                                if (transactions.length === 0) return []; // Skip products with no transactions

                                // 1. Aggregate amounts by type and determine category
                                const aggregated: { [type: string]: { amount: number; category: string } } = {};
                                transactions.forEach(tx => {
                                    const amount = tx.debit ?? tx.credit ?? 0;
                                    let category = tx.category || ''; // *** Use explicit category from data first ***
                                    // Determine category based on first occurrence of the type
                                    if (!aggregated[tx.type]) {
                                        if (tx.type.toLowerCase().includes('correction')) {
                                             category = 'Manual corrections';
                                             // Optional: Refine correction category based on specific type if needed later
                                        } else if (!category && tx.credit !== undefined && tx.credit !== null) { // Use credit/debit as fallback if category missing
                                            category = 'CR';
                                        } else if (!category && tx.debit !== undefined && tx.debit !== null) {
                                            category = 'DR';
                                        }
                                        // Ensure category is one of the valid keys for groupedByType
                                        const validCategory = ['CR', 'DR', 'Manual corrections'].includes(category) ? category : (amount < 0 ? 'CR' : 'DR');
                                        aggregated[tx.type] = { amount: 0, category: validCategory };
                                    }
                                    aggregated[tx.type].amount += amount;
                                });

                                const aggregatedList = Object.entries(aggregated).map(([type, data]) => ({
                                    type,
                                    amount: data.amount,
                                    category: data.category
                                }));

                                // 2. Group aggregated list by category for rendering
                                const groupedByType: { [category: string]: { type: string; amount: number }[] } = { 'CR': [], 'DR': [], 'Manual corrections': [] };
                                aggregatedList.forEach(item => {
                                    const categoryKey = item.category;
                                    if (groupedByType[categoryKey]) {
                                        groupedByType[categoryKey].push({ type: item.type, amount: item.amount });
                                    } else {
                                        // Fallback for unexpected/missing category
                                        const fallbackCategory = item.amount < 0 ? 'CR' : 'DR';
                                        if (!groupedByType[fallbackCategory]) groupedByType[fallbackCategory] = [];
                                        groupedByType[fallbackCategory].push({ type: item.type, amount: item.amount });
                                         console.warn(`Debit Tx type "${item.type}" had unexpected/missing category "${categoryKey}", assigned to ${fallbackCategory}`);
                                    }
                                });

                                // 3. Render rows
                                let isFirstRowOfProduct = true;
                                const productRows: React.ReactNode[] = [];
                                const categoryOrder = ['CR', 'DR', 'Manual corrections'];
                                const productTotalUniqueTypes = aggregatedList.length;

                                categoryOrder.forEach(categoryKey => {
                                    const categoryItems = groupedByType[categoryKey];

                                    if (categoryItems && categoryItems.length > 0) {
                                        categoryItems.forEach((item, index) => {
                                            const displayCategory = categoryKey;
                                            // Unique key combining product index, category, type
                                            const rowKey = `debit-${productIndex}-${displayCategory}-${item.type}`;

                                            productRows.push(
                                                <tr
                                                    key={rowKey}
                                                    style={{
                                                        borderBottom: '1px solid #f3f4f6',
                                                        backgroundColor: hoveredRowIndex === rowKey ? '#f9fafb' : 'transparent',
                                                        transition: 'background-color 0.1s ease-in-out'
                                                    }}
                                                    onMouseEnter={() => setHoveredRowIndex(rowKey)}
                                                    onMouseLeave={() => setHoveredRowIndex(null)}
                                                >
                                                    {isFirstRowOfProduct && (
                                                        <td rowSpan={productTotalUniqueTypes} style={{ padding: '9px 6px', fontWeight: '500', color: '#111827', verticalAlign: 'top' }}>
                                                            <span style={{ backgroundColor: '#f3f4f6', color: '#374151', padding: '3px 8px', borderRadius: '4px', fontSize: '12px', display: 'inline-block', fontWeight: '600' }}>
                                                                {product}
                                                            </span>
                                                        </td>
                                                    )}
                                                    {index === 0 && ( // Render category cell only for the first item in this category group
                                                        <td rowSpan={categoryItems.length} style={{ padding: '9px 6px', fontWeight: '500', color: '#111827', verticalAlign: 'top' }}>
                                                            <span style={{
                                                                padding: '3px 10px',
                                                                borderRadius: '12px',
                                                                fontSize: '12px',
                                                                fontWeight: '500',
                                                                textTransform: 'capitalize',
                                                                backgroundColor: displayCategory === 'CR' ? '#ecfdf5' : displayCategory === 'DR' ? '#fef2f2' : '#eef2ff', // Green, Red, Purple
                                                                color: displayCategory === 'CR' ? '#059669' : displayCategory === 'DR' ? '#ef4444' : '#4f46e5' // Green, Red, Purple
                                                            }}>
                                                                {displayCategory === 'CR' ? 'Credit' : displayCategory === 'DR' ? 'Debit' : displayCategory}
                                                            </span>
                                                        </td>
                                                    )}
                                                    <td style={{ padding: '9px 6px', color: '#374151' }}>{item.type}</td>
                                                    <td style={{
                                                        padding: '9px 6px',
                                                        textAlign: 'right',
                                                        color: item.amount < 0 ? '#ef4444' : '#111827',
                                                        fontWeight: '400'
                                                        }}>
                                                        {/* Format aggregated amount using base currency ('All' country) */}
                                                        {formatCurrency(item.amount, selectedCountry)}
                                                    </td>
                                                </tr>
                                            );
                                            isFirstRowOfProduct = false; // Ensure product cell only renders once
                                        });
                                    }
                                });

                                // Add divider row if not the last product
                                if (productIndex < displayedDebitTransactionsData.length - 1) {
                                    productRows.push(
                                        <tr key={`debit-divider-${productIndex}`}>
                                            <td colSpan={4} style={{ borderTop: '2px solid #e5e7eb', height: '1px', padding: '8px 0' }}></td>
                                        </tr>
                                    );
                                }

                                return productRows;
                            }) // End flatMap
                            // *** END REFACTORED LOGIC ***
                        )}
                     </tbody>
                 </table>
             </div>

            {/* Column 2: Credit Transaction Table Container */}
            {/* *** UPDATED: Removed horizontal padding from container *** */}
            <div style={{ backgroundColor: 'white', padding: '20px 0', borderRadius: '8px' }}>
                {/* Transaction Table - Credit Products */}
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}> {/* Reduced font size slightly */}
                    <thead>
                        {/* Apply Partners Table Header Style */}
                        <tr style={{ borderBottom: '1px solid #e5e7eb', color: '#6b7280', textAlign: 'left' }}>
                            {/* *** UPDATED: Adjust widths if necessary for the new column layout *** */}
                            <th style={{ padding: '8px 6px', fontWeight: '500', width: '20%' }}>Product</th>
                            <th style={{ padding: '8px 6px', fontWeight: '500', width: '25%' }}>Transaction category</th>
                            <th style={{ padding: '8px 6px', fontWeight: '500', width: '35%' }}>Transaction type</th>
                            <th style={{ padding: '8px 6px', fontWeight: '500', textAlign: 'right', width: '20%' }}>Amount</th> {/* Ensure right align for header too */}
                        </tr>
                    </thead>
                    <tbody>
                        {displayedCreditTransactionsData.length === 0 ? ( // *** Use Credit Data ***
                             // Apply Partners Table Empty State Style
                             <tr>
                                 <td colSpan={4} style={{ textAlign: 'center', padding: '40px 20px', color: '#6b7280', borderTop: '1px solid #e5e7eb' }}>
                                    {/* Simple Document Search Icon */}
                                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: '16px', color: '#d1d5db' }}>
                                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                                        <polyline points="14 2 14 8 20 8"></polyline>
                                        {/* Removed search icon elements from document icon */}
                                        <line x1="8" y1="13" x2="16" y2="13"></line>
                                        <line x1="8" y1="17" x2="12" y2="17"></line>
                                    </svg>
                                    <p style={{ fontSize: '14px', fontWeight: '500', margin: 0, color: '#111827' }}>No Transactions Found</p> {/* Match text */}
                                    <p style={{ fontSize: '13px', color: '#9ca3af', marginTop: '4px' }}>Try adjusting your date, product, or country filters.</p> {/* Adjusted message */}
                                </td>
                            </tr>
                        ) : (
                             // *** START REFACTORED LOGIC ***
                             displayedCreditTransactionsData.flatMap(({ product, transactions }, productIndex) => { // *** Use Credit Data ***
                                if (transactions.length === 0) return []; // Skip products with no transactions

                                // 1. Aggregate amounts by type and determine category
                                const aggregated: { [type: string]: { amount: number; category: string } } = {};
                                transactions.forEach(tx => {
                                    const amount = tx.debit ?? tx.credit ?? 0;
                                    let category = tx.category || ''; // *** Use explicit category from data first ***
                                    // Determine category based on first occurrence of the type
                                    if (!aggregated[tx.type]) {
                                        if (tx.type.toLowerCase().includes('correction')) {
                                             category = 'Manual corrections';
                                        } else if (!category && tx.credit !== undefined && tx.credit !== null) { // Use credit/debit as fallback if category missing
                                            category = 'CR';
                                        } else if (!category && tx.debit !== undefined && tx.debit !== null) {
                                            category = 'DR';
                                        }
                                        // Ensure category is one of the valid keys for groupedByType
                                        const validCategory = ['CR', 'DR', 'Manual corrections'].includes(category) ? category : (amount < 0 ? 'CR' : 'DR');
                                        aggregated[tx.type] = { amount: 0, category: validCategory };
                                    }
                                    aggregated[tx.type].amount += amount;
                                });

                                const aggregatedList = Object.entries(aggregated).map(([type, data]) => ({
                                    type,
                                    amount: data.amount,
                                    category: data.category
                                }));

                                // 2. Group aggregated list by category for rendering
                                const groupedByType: { [category: string]: { type: string; amount: number }[] } = { 'CR': [], 'DR': [], 'Manual corrections': [] };
                                aggregatedList.forEach(item => {
                                    const categoryKey = item.category;
                                    if (groupedByType[categoryKey]) {
                                        groupedByType[categoryKey].push({ type: item.type, amount: item.amount });
                                    } else {
                                         // Fallback for unexpected/missing category
                                        const fallbackCategory = item.amount < 0 ? 'CR' : 'DR';
                                        if (!groupedByType[fallbackCategory]) groupedByType[fallbackCategory] = [];
                                        groupedByType[fallbackCategory].push({ type: item.type, amount: item.amount });
                                         console.warn(`Credit Tx type "${item.type}" had unexpected/missing category "${categoryKey}", assigned to ${fallbackCategory}`);
                                    }
                                });

                                // 3. Render rows
                                let isFirstRowOfProduct = true;
                                const productRows: React.ReactNode[] = [];
                                const categoryOrder = ['CR', 'DR', 'Manual corrections'];
                                const productTotalUniqueTypes = aggregatedList.length;

                                categoryOrder.forEach(categoryKey => {
                                    const categoryItems = groupedByType[categoryKey];

                                    if (categoryItems && categoryItems.length > 0) {
                                        categoryItems.forEach((item, index) => {
                                            const displayCategory = categoryKey;
                                            const rowKey = `credit-${productIndex}-${displayCategory}-${item.type}`;

                                            productRows.push(
                                                <tr
                                                    key={rowKey}
                                                    style={{
                                                        borderBottom: '1px solid #f3f4f6',
                                                        backgroundColor: hoveredRowIndex === rowKey ? '#f9fafb' : 'transparent',
                                                        transition: 'background-color 0.1s ease-in-out'
                                                    }}
                                                    onMouseEnter={() => setHoveredRowIndex(rowKey)}
                                                    onMouseLeave={() => setHoveredRowIndex(null)}
                                                >
                                                    {isFirstRowOfProduct && (
                                                        <td rowSpan={productTotalUniqueTypes} style={{ padding: '9px 6px', fontWeight: '500', color: '#111827', verticalAlign: 'top' }}>
                                                            <span style={{ backgroundColor: '#f3f4f6', color: '#374151', padding: '3px 8px', borderRadius: '4px', fontSize: '12px', display: 'inline-block', fontWeight: '600' }}>
                                                                {product}
                                                            </span>
                                                        </td>
                                                    )}
                                                    {index === 0 && (
                                                        <td rowSpan={categoryItems.length} style={{ padding: '9px 6px', fontWeight: '500', color: '#111827', verticalAlign: 'top' }}>
                                                            <span style={{
                                                                padding: '3px 10px',
                                                                borderRadius: '12px',
                                                                fontSize: '12px',
                                                                fontWeight: '500',
                                                                textTransform: 'capitalize',
                                                                backgroundColor: displayCategory === 'CR' ? '#ecfdf5' : displayCategory === 'DR' ? '#fef2f2' : '#eef2ff', // Green, Red, Purple
                                                                color: displayCategory === 'CR' ? '#059669' : displayCategory === 'DR' ? '#ef4444' : '#4f46e5' // Green, Red, Purple
                                                            }}>
                                                                {displayCategory === 'CR' ? 'Credit' : displayCategory === 'DR' ? 'Debit' : displayCategory}
                                                            </span>
                                                        </td>
                                                    )}
                                                    <td style={{ padding: '9px 6px', color: '#374151' }}>{item.type}</td>
                                                    <td style={{
                                                        padding: '9px 6px',
                                                        textAlign: 'right',
                                                        color: item.amount < 0 ? '#ef4444' : '#111827',
                                                        fontWeight: '400'
                                                        }}>
                                                        {/* Format aggregated amount using base currency ('All' country) */}
                                                        {formatCurrency(item.amount, selectedCountry)}
                                                    </td>
                                                </tr>
                                            );
                                            isFirstRowOfProduct = false;
                                        });
                                    }
                                });

                                // Add divider row if not the last product
                                if (productIndex < displayedCreditTransactionsData.length - 1) {
                                    productRows.push(
                                        <tr key={`credit-divider-${productIndex}`}>
                                            <td colSpan={4} style={{ borderTop: '2px solid #e5e7eb', height: '1px', padding: '8px 0' }}></td>
                                        </tr>
                                    );
                                }

                                return productRows;
                            }) // End flatMap
                            // *** END REFACTORED LOGIC ***
                        )}
                     </tbody>
                 </table>
            </div>
         </div> {/* End two-column grid */}
          {/* Placeholder for other product transaction tables - REMOVED */}
       </section>


      {/* Partners Section - Updated styling for container and elements */}
      {/* *** UPDATED: Removed padding from section, rely on parent padding *** */}
      <section style={{ marginTop: '24px', backgroundColor: 'white', borderRadius: '8px' }}> {/* padding REMOVED */}
         {/* UPDATED: Header layout - Title/View All only */}
         {/* *** UPDATED: Add horizontal padding to match removed section padding *** */}
         <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}> {/* Updated marginBottom, removed padding */}
             {/* Left Side: Title, View All */}
             <div style={{ display: 'flex', alignItems: 'baseline', gap: '24px' }}> {/* Align by baseline, Increased gap */}
                <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600', color: '#111827' }}>Top Partners</h2> {/* Renamed header */}
                <button
                   style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#059669', fontSize: '14px', fontWeight: '500', padding: 0 }}
                   onClick={() => navigate('/partners')} // Add navigation onClick
                >
                    View all &gt;
                </button>
             </div>
             {/* Right Side: Removed */}
         </div>

         {/* UPDATED: Quick Filters and Search Bar */}
         {/* *** UPDATED: Add horizontal padding to match removed section padding *** */}
         <div style={{ marginTop: '12px', marginBottom: '20px', paddingBottom: '10px' }}>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}> {/* Align items vertically */}
                {/* Quick Filters */}
                {['All', 'Active', 'Requires Attention', 'BNPL'].map(filter => (
                    <button
                        key={filter}
                        onClick={() => setSelectedPartnerFilter(filter)}
                        style={{
                            padding: '6px 12px',
                            borderRadius: '6px',
                            border: selectedPartnerFilter === filter ? '1px solid transparent' : '1px solid #d1d5db',
                            background: selectedPartnerFilter === filter ? '#eef2ff' : 'white', // Light indigo for selected
                            color: selectedPartnerFilter === filter ? '#4f46e5' : '#374151', // Indigo text for selected
                            cursor: 'pointer',
                            fontSize: '13px',
                            fontWeight: '500',
                            transition: 'all 0.15s ease-in-out'
                        }}
                        onMouseEnter={(e) => { if (selectedPartnerFilter !== filter) e.currentTarget.style.borderColor = '#adb5bd'; }}
                        onMouseLeave={(e) => { if (selectedPartnerFilter !== filter) e.currentTarget.style.borderColor = '#d1d5db'; }}
                    >
                        {filter}
                    </button>
                ))}
                {/* Search input moved here and styled like filters */}
                <input
                    type="search"
                    placeholder="Search partners..."
                    style={{
                        padding: '6px 12px',        // Match filter padding
                        borderRadius: '6px',       // Match filter border radius
                        border: '1px solid #d1d5db', // Match filter border
                        fontSize: '13px',          // Match filter font size
                        fontWeight: '400',        // Regular weight for input placeholder
                        color: '#374151',          // Match filter text color
                        backgroundColor: 'white',
                        minWidth: '200px',       // Adjust width as needed
                        marginLeft: 'auto'         // Push search to the right
                    }}
                    value={searchQuery} // *** NEW: Bind value to state ***
                    onChange={(e) => setSearchQuery(e.target.value)} // *** NEW: Update state on change ***
                 />
            </div>
        </div>


        {/* Partners Table - Updated styling */}
        {filteredPartners.length > 0 ? (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
               <thead>
                 {/* Lighter header border and text color */}
                 <tr style={{ borderBottom: '1px solid #e5e7eb', textAlign: 'left', color: '#6b7280' }}>
                   {/* *** UPDATED: Add left/right padding to header cells *** */}
                   <th style={{ padding: '12px 24px 12px 24px', fontWeight: '500' }}>Partner</th>
                   <th style={{ padding: '12px 8px', fontWeight: '500' }}>Products</th>
                   <th style={{ padding: '12px 8px', fontWeight: '500' }}>Debit Balance</th>
                   <th style={{ padding: '12px 8px', fontWeight: '500' }}>Credit Balance</th>
                   <th style={{ padding: '12px 8px', fontWeight: '500' }}>Status</th>
                   <th style={{ padding: '12px 8px', fontWeight: '500' }}>Risk Score</th>
                   <th style={{ padding: '12px 8px', fontWeight: '500' }}>Last Payout</th>
                   {/* *** UPDATED: Add left/right padding to action header cell *** */}
                   <th style={{ padding: '12px 24px 12px 8px' }}></th> {/* Actions column */}
                 </tr>
               </thead>
               <tbody>
                 {filteredPartners.map((partner, index) => (
                   <tr
                      key={partner.name} // Use a stable key like partner name or ID
                      style={{
                        borderBottom: '1px solid #f3f4f6', // Very light row border
                        cursor: 'pointer', // Make row clickable
                        backgroundColor: hoveredRowIndex === partner.name ? '#f9fafb' : 'transparent', // Hover effect using key
                        transition: 'background-color 0.1s ease-in-out' // Smooth transition
                      }}
                      onClick={() => handlePartnerClick(partner.name)} // Add click handler
                      onMouseEnter={() => setHoveredRowIndex(partner.name)} // Set hover state using key
                      onMouseLeave={() => setHoveredRowIndex(null)} // Clear hover state
                    >
                     {/* *** UPDATED: Add left/right padding to first cell *** */}
                     <td style={{ padding: '12px 8px 12px 24px', display: 'flex', alignItems: 'center' }}>
                       {/* Use img tag if logoUrl exists, otherwise use fallback */}
                       {partner.logoUrl ? (
                         <img
                           src={partner.logoUrl}
                           alt={`${partner.name} logo`}
                           style={{
                             marginRight: '10px', // Increased margin
                             width: '24px',
                             height: '24px',
                             borderRadius: '50%',
                             objectFit: 'contain',
                             border: '1px solid #f3f4f6' // Very light border
                           }}
                           onError={(e) => { // Fallback for broken image links or loading errors
                             const imgElement = e.target as HTMLImageElement;
                             imgElement.style.display = 'none'; // Hide broken image icon
                             // Find and display the sibling fallback span
                             const fallbackSpan = imgElement.nextElementSibling as HTMLElement | null;
                             if (fallbackSpan && fallbackSpan.classList.contains('logo-fallback')) {
                               fallbackSpan.style.display = 'flex';
                               fallbackSpan.style.marginRight = '10px'; // Ensure margin is applied on error
                             }
                           }}
                         />
                       ) : null}
                       {/* Fallback Placeholder (initially hidden if logoUrl exists, shown if null or onError) */}
                       <span
                           className="logo-fallback" // Class for targeting in onError
                           style={{
                               marginRight: '10px', // Increased margin
                               display: partner.logoUrl ? 'none' : 'flex', // Display flex if no logoUrl
                               width: '24px',
                               height: '24px',
                               borderRadius: '50%',
                               backgroundColor: '#f3f4f6', // Light gray fallback bg
                               alignItems: 'center',
                               justifyContent: 'center',
                               fontSize: '10px',
                               fontWeight: '500', // Medium weight
                               color: '#6b7280', // Medium gray text
                               textTransform: 'uppercase'
                           }}>
                               {/* Generate initials */}
                               {partner.name.split(' ').map(n => n?.[0]).filter(Boolean).slice(0, 2).join('')} {/* Safer initial generation */}
                       </span>
                       <div>
                         {/* Darker text for partner name */}
                         <div style={{ fontWeight: '500', color: '#111827' }}>{partner.name}</div>
                         {/* Lighter text for location */}
                         <div style={{ fontSize: '12px', color: '#6b7280' }}>{partner.location}</div>
                       </div>
                     </td>
                     <td style={{ padding: '12px 8px' }}>
                       {/* Lighter product tags */}
                       {partner.products.map(p => <span key={p} style={{ backgroundColor: '#f3f4f6', color: '#374151', padding: '3px 8px', borderRadius: '4px', marginRight: '5px', fontSize: '12px', display: 'inline-block', marginBottom: '4px' }}>{p}</span>)}
                     </td>
                     {/* Darker text for balance numbers */}
                     <td style={{ padding: '12px 8px', color: '#111827' }}>{formatCurrency(partner.baseDebit, partner.location)}</td>
                     <td style={{ padding: '12px 8px', color: '#111827' }}>{formatCurrency(partner.baseCredit, partner.location)}</td>
                     <td style={{ padding: '12px 8px' }}>
                        <StatusBadge status={partner.status as 'Active' | 'Inactive' | 'Pending'} />
                     </td>
                     {/* Adjusted risk score color logic */}
                     <td style={{ padding: '12px 8px', color: parseInt(partner.risk) >= 90 ? '#059669' : (parseInt(partner.risk) < 70 ? '#f43f5e' : '#4b5563') }}>{partner.risk}</td>
                     <td style={{ padding: '12px 8px', color: '#6b7280' }}>{partner.lastPayout}</td>
                     {/* *** UPDATED: Add left/right padding to action cell *** */}
                     <td style={{ padding: '12px 24px 12px 8px', textAlign: 'center' }}>
                         {/* Lighter action button placeholder */}
                         <button style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#9ca3af' }}>...</button>
                     </td>
                   </tr>
                 ))}
               </tbody>
            </table>
        ) : (
            // *** NEW: Empty State ***
            <div style={{
                textAlign: 'center',
                padding: '40px 20px',
                color: '#6b7280', // Medium gray text
                borderTop: '1px solid #e5e7eb', // Match header border
                marginTop: '20px', // Add some space from filters
                // *** UPDATED: Add horizontal padding for empty state ***
                margin: '20px 24px 0 24px'
            }}>
                {/* Simple Document Search Icon */}
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: '16px', color: '#d1d5db' }}> {/* Lighter icon color */}
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                    <polyline points="14 2 14 8 20 8"></polyline>
                    <circle cx="11.5" cy="11.5" r="2.5"></circle>
                    <line x1="13.5" y1="13.5" x2="17.5" y2="17.5"></line>
                </svg>
                <p style={{ fontSize: '14px', fontWeight: '500', margin: 0 }}>No Partners Found</p>
                <p style={{ fontSize: '13px', color: '#9ca3af', marginTop: '4px' }}>Try adjusting your search or filter criteria.</p>
            </div>
        )}
      </section>
    </div>
  </MainLayout>
);
}

// *** NEW: Transaction Formatting Helpers ***

// Always formats using the base currency ($) for table amounts
const formatTableBaseAmount = (baseAmount: number | undefined): string => {
    if (baseAmount === undefined || baseAmount === null) return '';
    // Always use $ for table base amounts
    return `$ ${Math.round(baseAmount).toLocaleString()}`;
};

// --- NEW: Transaction Data Definitions (Moved to global scope, using base $ amounts) ---

const uaeTransactions: TransactionRow[] = [
    // BNPL - CR (Example for UAE, structure based on image)
    { type: 'Sale', credit: 954949112, category: 'CR' }, // Increased
    { type: 'Refundable fee', credit: 345345, category: 'CR' }, // Increased
    { type: 'Non Refundable fee', credit: 345345, category: 'CR' }, // Increased
    { type: 'Fixed Fee', credit: 345345, category: 'CR' }, // Increased
    { type: 'VAT', credit: 34534535, category: 'CR' }, // Increased
    { type: 'Payout Fees', credit: 34535, category: 'CR' }, // Increased
    { type: 'Collections', credit: 34534535, category: 'CR' }, // Increased
    // BNPL - DR
    { type: 'Refund', debit: -34535, category: 'DR' }, // Increased
    { type: 'Refundable fee', debit: -34535, category: 'DR' }, // Increased
    { type: 'VAT', debit: -345345, category: 'DR' }, // Increased
    { type: 'Withdrawal', debit: -34535, category: 'DR' }, // Increased
    // BNPL - Manual Corrections
    { type: 'Revenue corrections', debit: 345345, category: 'Manual corrections' }, // Increased
    { type: 'Non-revenue corrections', debit: -2344, category: 'Manual corrections' }, // Increased
 ];
 const ksaTransactions: TransactionRow[] = [
    // Mirroring UAE structure for KSA with different amounts
    { type: 'Sale', credit: 1254949112, category: 'CR' }, // Increased
    { type: 'Refundable fee', credit: 445345, category: 'CR' }, // Increased
    { type: 'Fixed Fee', credit: 445345, category: 'CR' }, // Increased
    { type: 'VAT', credit: 44534535, category: 'CR' }, // Increased
    { type: 'Collections', credit: 44534535, category: 'CR' }, // Increased
    { type: 'Refund', debit: -44535, category: 'DR' }, // Increased
    { type: 'VAT', debit: -445345, category: 'DR' }, // Increased
    { type: 'Withdrawal', debit: -44535, category: 'DR' }, // Increased
    { type: 'Revenue corrections', debit: 445345, category: 'Manual corrections' }, // Increased
    { type: 'Non-revenue corrections', debit: -3344, category: 'Manual corrections' }, // Increased
 ];
 const qatarTransactions: TransactionRow[] = [
    // Simpler structure for smaller countries maybe?
    { type: 'Sale', credit: 35494912, category: 'CR' }, // Increased
    { type: 'VAT', credit: 345345, category: 'CR' }, // Increased
    { type: 'Collections', credit: 345345, category: 'CR' }, // Increased
    { type: 'Refund', debit: -3453, category: 'DR' }, // Increased
    { type: 'VAT', debit: -34534, category: 'DR' }, // Increased
    { type: 'Revenue corrections', debit: 34534, category: 'Manual corrections' }, // Increased
 ];
 const kuwaitTransactions: TransactionRow[] = [
    { type: 'Sale', credit: 25494912, category: 'CR' }, // Increased
    { type: 'VAT', credit: 245345, category: 'CR' }, // Increased
    { type: 'Collections', credit: 245345, category: 'CR' }, // Increased
    { type: 'Refund', debit: -2453, category: 'DR' }, // Increased
    { type: 'VAT', debit: -24534, category: 'DR' }, // Increased
    { type: 'Non-revenue corrections', debit: -1344, category: 'Manual corrections' }, // Increased
 ];
 const bahrainTransactions: TransactionRow[] = [
    { type: 'Sale', credit: 15494912, category: 'CR' }, // Increased
    { type: 'VAT', credit: 145345, category: 'CR' }, // Increased
    { type: 'Collections', credit: 145345, category: 'CR' }, // Increased
    { type: 'Refund', debit: -1453, category: 'DR' }, // Increased
    { type: 'VAT', debit: -14534, category: 'DR' }, // Increased
 ];
 
 const acquiringTransactionsUAE: TransactionRow[] = [
    // Acquiring - CR
    { type: 'Sale', credit: 95494911, category: 'CR' }, // Increased
    { type: 'Sale commission', credit: 345345, category: 'CR' }, // Increased
    { type: 'Fixed Fee', credit: 345345, category: 'CR' }, // Increased
    { type: 'VAT', credit: 3453453, category: 'CR' }, // Increased
    // Acquiring - DR
    { type: 'Refund', debit: -3453, category: 'DR' }, // Increased
    { type: 'Refund commission', debit: -234234, category: 'DR' }, // Increased
    { type: 'VAT', debit: -23424, category: 'DR' }, // Increased
    { type: 'Withdrawal', debit: -34535, category: 'DR' }, // Increased
    // Acquiring - Manual Corrections
    { type: 'Revenue corrections', debit: 23424, category: 'Manual corrections' }, // Increased
    { type: 'Non-revenue corrections', debit: -24242, category: 'Manual corrections' }, // Increased
 ];
 const acquiringTransactionsKSA: TransactionRow[] = [
    // Mirroring UAE structure for KSA
    { type: 'Sale', credit: 105494911, category: 'CR' }, // Increased
    { type: 'Sale commission', credit: 445345, category: 'CR' }, // Increased
    { type: 'Fixed Fee', credit: 445345, category: 'CR' }, // Increased
    { type: 'VAT', credit: 4453453, category: 'CR' }, // Increased
    { type: 'Refund', debit: -4453, category: 'DR' }, // Increased
    { type: 'Refund commission', debit: -334234, category: 'DR' }, // Increased
    { type: 'VAT', debit: -33424, category: 'DR' }, // Increased
    { type: 'Withdrawal', debit: -44535, category: 'DR' }, // Increased
    { type: 'Revenue corrections', debit: 33424, category: 'Manual corrections' }, // Increased
    { type: 'Non-revenue corrections', debit: -34242, category: 'Manual corrections' }, // Increased
 ];
 const acquiringTransactionsQatar: TransactionRow[] = [
    { type: 'Sale', credit: 3549491, category: 'CR' }, // Increased
    { type: 'VAT', credit: 34534, category: 'CR' }, // Increased
    { type: 'Refund', debit: -3453, category: 'DR' }, // Increased
    { type: 'VAT', debit: -2342, category: 'DR' }, // Increased
    { type: 'Withdrawal', debit: -3453, category: 'DR' }, // Increased
 ];
 const acquiringTransactionsKuwait: TransactionRow[] = [
    { type: 'Sale', credit: 2549491, category: 'CR' }, // Increased
    { type: 'VAT', credit: 24534, category: 'CR' }, // Increased
    { type: 'Refund', debit: -2453, category: 'DR' }, // Increased
    { type: 'VAT', debit: -2342, category: 'DR' }, // Increased
 ];
 const acquiringTransactionsBahrain: TransactionRow[] = [
    { type: 'Sale', credit: 1549491, category: 'CR' }, // Increased
    { type: 'VAT', credit: 14534, category: 'CR' }, // Increased
    { type: 'Refund', debit: -1453, category: 'DR' }, // Increased
    { type: 'VAT', debit: -1453, category: 'DR' }, // Increased
    { type: 'Non-revenue corrections', debit: -1424, category: 'Manual corrections' }, // Increased
 ];
 
 const prepaidAdsTransactionsUAE: TransactionRow[] = [
    { type: 'Ads top up', credit: 3453453, category: 'CR' }, // Increased
    { type: 'Ads spend', debit: 2342424, category: 'DR' }, // Increased
    { type: 'Non-revenue corrections', debit: 24242, category: 'Manual corrections' }, // Increased
 ];
 const prepaidAdsTransactionsKSA: TransactionRow[] = [
    { type: 'Ads top up', credit: 4453453, category: 'CR' }, // Increased
    { type: 'Ads spend', debit: 3342424, category: 'DR' }, // Increased
    { type: 'Non-revenue corrections', debit: 34242, category: 'Manual corrections' }, // Increased
 ];
 const prepaidAdsTransactionsQatar: TransactionRow[] = [
    { type: 'Ads top up', credit: 145345, category: 'CR' }, // Increased
    { type: 'Ads spend', debit: 134242, category: 'DR' }, // Increased
 ];
 const prepaidAdsTransactionsKuwait: TransactionRow[] = [
    { type: 'Ads top up', credit: 105345, category: 'CR' }, // Increased
    { type: 'Ads spend', debit: 94242, category: 'DR' }, // Increased
 ];
 const prepaidAdsTransactionsBahrain: TransactionRow[] = [
    { type: 'Ads top up', credit: 85345, category: 'CR' }, // Increased
    { type: 'Ads spend', debit: 74242, category: 'DR' }, // Increased
    { type: 'Non-revenue corrections', debit: 1424, category: 'Manual corrections' }, // Increased
 ];
 
 const capitalTransactions: TransactionRow[] = [
    // Capital - CR
    { type: 'Disbursements', credit: 94949112, category: 'CR' }, // Increased
    { type: 'Late repayment fee', credit: 345345, category: 'CR' }, // Increased
    { type: 'VAT', credit: 345345, category: 'CR' }, // Increased
    // Capital - DR
    { type: 'Repayment', debit: 345345, category: 'DR' }, // Increased
    { type: 'Late fees', debit: 3453453, category: 'DR' }, // Increased
    { type: 'VAT', debit: 34535, category: 'DR' }, // Increased
    // Capital - Manual Corrections
    { type: 'Revenue corrections', debit: 3453453, category: 'Manual corrections' }, // Increased
    { type: 'Non-revenue corrections', debit: 345345, category: 'Manual corrections' }, // Increased
 ];
 
 const tabbyCardTransactions: TransactionRow[] = [
    // Tabby Card - CR
    { type: 'Sale', credit: -34535, category: 'CR' }, // Increased
    { type: 'Commission', credit: -345345, category: 'CR' }, // Increased
    { type: 'VAT', credit: -34535, category: 'CR' }, // Increased
    { type: 'Commission collection', credit: -345345, category: 'CR' }, // Increased
    // Tabby Card - DR
    { type: 'Refund', debit: 2344, category: 'DR' }, // Increased
    { type: 'Refund commission', debit: -34535, category: 'DR' }, // Increased
    { type: 'VAT', debit: -234234, category: 'DR' }, // Increased
    // Tabby Card - Manual Corrections
    { type: 'Revenue corrections', debit: -23424, category: 'Manual corrections' }, // Increased
    { type: 'Non-revenue corrections', debit: -34535, category: 'Manual corrections' }, // Increased
 ];
 
 const adsPostpaidTransactions: TransactionRow[] = [
    // Ads Postpaid - CR (Following image even if counter-intuitive)
    { type: 'Ads spend', credit: 95494911, category: 'CR' }, // Increased
    // Ads Postpaid - DR
    { type: 'Ads top up', debit: 345345, category: 'DR' }, // Increased
    // Ads Postpaid - Manual Corrections
    { type: 'Non-revenue corrections', debit: 3453453, category: 'Manual corrections' }, // Increased
 ];
 
 // --- Helper function to get transactions for a specific product and country ---
 const getProductTransactions = (product: string, country: string): TransactionRow[] => {
    switch (product) {
        case 'BNPL':
            switch (country) {
                case 'UAE': return uaeTransactions;
                case 'KSA': return ksaTransactions;
                case 'Qatar': return qatarTransactions;
                case 'Kuwait': return kuwaitTransactions;
                case 'Bahrain': return bahrainTransactions;
                default: return [...uaeTransactions, ...ksaTransactions, ...qatarTransactions, ...kuwaitTransactions, ...bahrainTransactions]; // Aggregate for 'All'
            }
        case 'Acquiring':
            switch (country) {
                case 'UAE': return acquiringTransactionsUAE;
                case 'KSA': return acquiringTransactionsKSA;
                case 'Qatar': return acquiringTransactionsQatar;
                case 'Kuwait': return acquiringTransactionsKuwait;
                case 'Bahrain': return acquiringTransactionsBahrain;
                default: return [...acquiringTransactionsUAE, ...acquiringTransactionsKSA, ...acquiringTransactionsQatar, ...acquiringTransactionsKuwait, ...acquiringTransactionsBahrain]; // Aggregate for 'All'
            }
        // *** NEW: Add case for Prepaid Ads ***
        case 'Prepaid Ads':
            switch (country) {
                case 'UAE': return prepaidAdsTransactionsUAE;
                case 'KSA': return prepaidAdsTransactionsKSA;
                case 'Qatar': return prepaidAdsTransactionsQatar;
                case 'Kuwait': return prepaidAdsTransactionsKuwait;
                case 'Bahrain': return prepaidAdsTransactionsBahrain;
                default: return [...prepaidAdsTransactionsUAE, ...prepaidAdsTransactionsKSA, ...prepaidAdsTransactionsQatar, ...prepaidAdsTransactionsKuwait, ...prepaidAdsTransactionsBahrain]; // Aggregate for 'All'
            }
        // Add cases for other products here if needed
        // *** NEW: Cases for Credit Products ***
        case 'Capital':
            // Capital data is country-agnostic
            return capitalTransactions;
        case 'Tabby Card':
            // Tabby Card data is country-agnostic
            return tabbyCardTransactions;
        case 'Postpaid Ads':
            // Postpaid Ads data is country-agnostic
            return adsPostpaidTransactions;
        default:
            return []; // Return empty for unknown products
    }
};

export default MerchantWalletDashboard;